import { useVirtualizer } from '@tanstack/react-virtual';
import { ExternalLink, Loader2 } from 'lucide-react';
import { memo, useRef } from 'react';
import type { LinkSummaryResponse } from '@/api/client';
import { MarkdownContent } from '@/components/MarkdownContent';
import { type TagData, TagList } from '@/components/TagDisplay';
import { cn } from '@/lib/utils';

// Format relative age: 45m, 2h, 3d, 2w (max unit is weeks)
function formatRelativeAge(dateStr: string): string {
	const now = Date.now();
	const date = new Date(dateStr).getTime();
	const diffMs = now - date;

	const minutes = Math.round(diffMs / (1000 * 60));
	if (minutes <= 90) return `${Math.max(1, minutes)}m`;

	const hours = Math.round(diffMs / (1000 * 60 * 60));
	if (hours < 24) return `${hours}h`;

	const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
	if (days < 7) return `${days}d`;

	const weeks = Math.round(days / 7);
	return `${weeks}w`;
}

interface LinkCardProps {
	link: LinkSummaryResponse;
	categoryColor: string;
	onClick: (e: React.MouseEvent) => void;
	onTagClick?: (tag: TagData) => void;
	isSelected?: boolean;
}

const LinkCard = memo(function LinkCard({
	link,
	categoryColor,
	onClick,
	onTagClick,
	isSelected,
}: LinkCardProps) {
	// Mute colors for terminal aesthetic: reduce saturation, darken
	const mutedColor = categoryColor; // Keep original for now, but use sparingly
	return (
		<div
			className={cn(
				'group relative p-3 cursor-pointer transition-all min-w-0 w-full select-none border',
				isSelected
					? 'border-primary bg-primary/5'
					: 'border-border bg-card hover:border-muted-foreground'
			)}
			style={{
				// Small colored indicator instead of full background
				borderLeftWidth: '3px',
				borderLeftColor: isSelected ? undefined : mutedColor,
			}}
			onClick={onClick}
		>
			{/* Link ID - top right, terminal style */}
			<span className="absolute top-1 right-1.5 text-[9px] text-muted-foreground font-mono">
				[{link.id}]
			</span>

			{/* Top section: Thumbnail LEFT + Title/Description RIGHT */}
			<div className="flex gap-3 min-w-0">
				{/* Thumbnail - LEFT */}
				{link.thumbnailUrl && (
					<img
						src={`/api/thumbnail/${link.id}`}
						alt=""
						className="size-14 object-cover bg-muted shrink-0 border border-border"
						loading="lazy"
						onError={(e) => {
							e.currentTarget.style.display = 'none';
						}}
					/>
				)}

				{/* Title + Description + External link */}
				<div className="flex-1 min-w-0 flex flex-col gap-1">
					<div className="flex items-start justify-between gap-2">
						<p className="font-medium text-sm line-clamp-2 font-mono">{link.title}</p>
						<a
							href={link.url}
							target="_blank"
							rel="noopener noreferrer"
							onClick={(e) => e.stopPropagation()}
							className="shrink-0 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
						>
							<ExternalLink className="size-4" />
						</a>
					</div>
					{link.description && (
						<div className="text-xs text-muted-foreground line-clamp-2">
							<MarkdownContent url={link.url}>{link.description}</MarkdownContent>
						</div>
					)}
				</div>
			</div>

			{/* Bottom section: Tags + metadata - full width under everything */}
			<div className="mt-2 flex items-center justify-between">
				{/* Tags */}
				<div className="flex items-center gap-2 min-w-0 flex-1">
					{link.tags && link.tags.length > 0 && (
						<TagList
							tags={link.tags as TagData[]}
							maxDisplay={4}
							onTagClick={onTagClick}
							showHiddenIndicator={false}
						/>
					)}
					{link.isEnriching && (
						<span title="Enriching...">
							<Loader2 className="size-3.5 animate-spin text-muted-foreground" />
						</span>
					)}
				</div>
				{/* Age (use updatedAt since createdAt not in slim response) */}
				<span className="text-xs text-muted-foreground shrink-0 font-mono">
					{formatRelativeAge(link.updatedAt)}
				</span>
			</div>
		</div>
	);
});

interface LinkListProps {
	links: LinkSummaryResponse[];
	onCardClick: (link: LinkSummaryResponse, event: React.MouseEvent) => void;
	onTagClick?: (tag: TagData) => void;
	selectedLinkIds: Set<number>;
	className?: string;
}

export function LinkList({
	links,
	onCardClick,
	onTagClick,
	selectedLinkIds,
	className,
}: LinkListProps) {
	const defaultColor = '#94a3b8';
	const parentRef = useRef<HTMLDivElement>(null);

	// Estimate card height: 88px base + thumbnail height if present
	// Most cards without thumbnail: ~72px, with thumbnail: ~88px
	const virtualizer = useVirtualizer({
		count: links.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 88,
		overscan: 5,
	});

	if (links.length === 0) {
		return (
			<div className={cn('flex items-center justify-center h-full', className)}>
				<p className="text-muted-foreground">No links in this queue</p>
			</div>
		);
	}

	const items = virtualizer.getVirtualItems();

	return (
		<div
			ref={parentRef}
			className={cn('h-full w-full overflow-y-auto overflow-x-hidden', className)}
		>
			{/* Padding wrapper */}
			<div className="p-3">
				{/* Virtual container with total height */}
				<div
					style={{
						height: `${virtualizer.getTotalSize()}px`,
						position: 'relative',
					}}
				>
					{/* Virtual items - absolutely positioned */}
					{items.map((virtualRow) => {
						const link = links[virtualRow.index];
						// Get color from first category tag, or use default
						const categoryTag = link.tags?.find((t) => t.name.startsWith('category:'));
						const color = categoryTag?.color ?? defaultColor;

						return (
							<div
								key={`${link.id}-${link.updatedAt}`}
								data-index={virtualRow.index}
								ref={virtualizer.measureElement}
								style={{
									position: 'absolute',
									top: 0,
									left: 0,
									width: '100%',
									transform: `translateY(${virtualRow.start}px)`,
									// Apply gap-2 (8px) as margin-bottom
									marginBottom: '8px',
								}}
							>
								<LinkCard
									link={link}
									categoryColor={color}
									onClick={(e) => onCardClick(link, e)}
									onTagClick={onTagClick}
									isSelected={selectedLinkIds.has(link.id)}
								/>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
